import type { ComparatorOp } from './nodes/comparator-control.component';
import type { FormulaFn } from './nodes/formula-control.component';
import type { LiteralKind } from './nodes/literal-control.component';

export type GraphConnection = {
  source: string;
  sourceOutput: string;
  target: string;
  targetInput: string;
};

export type GraphNodeInfo =
  | { kind: 'entityField'; nodeId: string; fieldName: string }
  | { kind: 'literal'; nodeId: string; literalKind: LiteralKind; value: unknown }
  | { kind: 'comparator'; nodeId: string; op: ComparatorOp }
  | {
      kind: 'formula';
      nodeId: string;
      fn: FormulaFn;
      digits?: number;
      joinChar?: string;
      dateFormat?: string;
      listaggOptions?: 'list' | 'distinct';
    };

function q(s: string): string {
  // シングルクォートは2重化
  return "'" + s.replace(/'/g, "''") + "'";
}

function emitLiteral(kind: LiteralKind, value: unknown): string {
  if (kind === 'null') return 'null';
  if (kind === 'number') return String(typeof value === 'number' ? value : Number(value) || 0);
  if (kind === 'string') return q(String(value ?? ''));
  if (kind === 'string[]') {
    const arr = Array.isArray(value) ? value : [];
    return `(${arr.map((x) => q(String(x))).join(',')})`;
  }
  return q(String(value ?? ''));
}

export function buildFormulaEmitter(nodes: GraphNodeInfo[], connections: GraphConnection[]) {
  const byId = new Map(nodes.map((n) => [n.nodeId, n] as const));
  const incoming = new Map<string, GraphConnection[]>();
  for (const c of connections) {
    const list = incoming.get(c.target) ?? [];
    list.push(c);
    incoming.set(c.target, list);
  }

  const memo = new Map<string, string>();
  const keyOf = (nodeId: string, out: string) => `${nodeId}:${out}`;

  const emitOutput = (nodeId: string, out: string): string => {
    const key = keyOf(nodeId, out);
    const cached = memo.get(key);
    if (cached !== undefined) return cached;

    const node = byId.get(nodeId);
    if (!node) return '';

    let result = '';
    if (node.kind === 'entityField') result = node.fieldName;
    if (node.kind === 'literal') result = emitLiteral(node.literalKind, node.value);
    if (node.kind === 'comparator') {
      const conns = (incoming.get(nodeId) ?? []).reduce(
        (acc, c) => {
          acc[c.targetInput] = c;
          return acc;
        },
        {} as Record<string, GraphConnection>,
      );
      const a = conns['a'] ? emitOutput(conns['a'].source, conns['a'].sourceOutput) : '';
      const b = conns['b'] ? emitOutput(conns['b'].source, conns['b'].sourceOutput) : '';
      result = a && b ? `${a} ${node.op} ${b}` : '';
    }
    if (node.kind === 'formula') {
      const conns = (incoming.get(nodeId) ?? []).reduce(
        (acc, c) => {
          acc[c.targetInput] = c;
          return acc;
        },
        {} as Record<string, GraphConnection>,
      );
      const args = ['a', 'b', 'c', 'd', 'e']
        .map((k) => (conns[k] ? emitOutput(conns[k].source, conns[k].sourceOutput) : ''))
        .filter((x) => x !== '');

      switch (node.fn) {
        case 'iif':
          result = args.length >= 3 ? `iif(${args[0]}, ${args[1]}, ${args[2]})` : '';
          break;
        case 'joinstring':
          result = `joinstring(${q(node.joinChar ?? '-')}${args.length ? ', ' + args.join(', ') : ''})`;
          break;
        case 'datestring':
          result = `datestring(${q(node.dateFormat ?? 'yyyyMMdd')}${args.length ? ', ' + args.join(', ') : ''})`;
          break;
        case 'round':
        case 'ceiling':
        case 'floor': {
          const digits = typeof node.digits === 'number' ? node.digits : 0;
          result = args.length >= 1 ? `${node.fn}(${args[0]}, ${digits})` : '';
          break;
        }
        case 'listagg':
          result = `listagg(${node.listaggOptions ?? 'list'}${args.length ? ', ' + args.join(', ') : ''})`;
          break;
        case 'count':
        case 'guid':
        case 'ulid':
        case 'now':
        case 'rowNo':
        case 'viewNo':
          result = `${node.fn}()`;
          break;
        default:
          result = `${node.fn}(${args.join(', ')})`;
      }
    }

    memo.set(key, result);
    return result;
  };

  const emitFromInput = (targetNodeId: string, inputKey: string): string => {
    const c = (incoming.get(targetNodeId) ?? []).find((x) => x.targetInput === inputKey);
    if (!c) return '';
    return emitOutput(c.source, c.sourceOutput);
  };

  return { emitFromInput, emitOutput };
}

